import {
  Button,
  Card,
  Col,
  Input,
  Popover,
  Row,
  Skeleton,
  Space,
  Table,
  Tooltip,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useTheme } from "../../Context/ThemeContext";
import { useClient } from "../../Context/ClientContext";
import { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { LedgerStatus } from "../../Services/LedgerService";
import { notificationContext } from "../../Common/PageRoute";
import OrganisationsService, {
  IDesignationData,
} from "../../Services/Organisations/OrganisationsService";
import { OrgDataType } from "./Organisations";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import { IIdNameModel } from "../../Services/ItemsService";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import AddDesignationModel from "./AddDesignationModel";

interface DesignationDataType extends OrgDataType {
  designationName: string;
  subUnitName: string;
  depttName: string;
}

const Designations = () => {
  const { currentClient } = useClient();
  const [openDesignationModel, setOpenDesignationModel] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [addUpdateTrigger, setAddUpdateTrigger] = useState<boolean>(false);
  const [searchDesignation, setSearchDesignation] = useState<string>("");
  const { themeData } = useTheme();
  const { openNotification } = useContext(notificationContext);
  const [designationsList, setDesignationsList] = useState<
    DesignationDataType[]
  >([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const getDesignationsList = async () => {
    setDataLoading(true);
    await OrganisationsService.getDesignationsList(
      currentClient?.id!,
      searchDesignation,
      "",
      "",
      LedgerStatus.active
    ).then((val: IDesignationData[] | null) => {
      setDesignationsList(
        val?.map<DesignationDataType>(
          (des: IDesignationData, index: number) => {
            return {
              sno: index + 1,
              id: des.id as string,
              designationName: des.designationName as string,
              orgName: des.orgName as string,
              subUnitName: des.subUnitName as string,
              depttName: des.depttName as string,
              status: des.status!,
            };
          }
        ) || []
      );
    });
    setDataLoading(false);
  };

  const handleDelete = async (selId: string) => {
    if (selId) {
      await OrganisationsService.deleteDesignation(currentClient?.id!, selId)
        .then((val: IApiResponse<IIdNameModel>) => {
          if (val?.status) {
            openNotification(
              "success",
              val?.result?.name + " designation deleted successfully"
            );
            setAddUpdateTrigger((prev) => !prev);
          } else {
            openNotification("error", val?.errors && val?.errors[0]?.message);
          }
        })
        .catch((err) => {
          openNotification("error", err?.message || "");
        });
      setDeleteConfirmation({
        open: false,
        id: "",
      });
    }
  };

  useEffect(() => {
    if (currentClient?.id) {
      getDesignationsList();
    }
  }, [currentClient?.id, addUpdateTrigger, searchDesignation]);

  // --------- Departments Table Columns  ---------
  const depttCols: ColumnsType<DesignationDataType> = [
    {
      title: "S.No.",
      dataIndex: "sno",
      key: "sno",
      width: "5%",
      render: (value: number) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Designation's Name",
      dataIndex: "designationName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Department's Name",
      dataIndex: "depttName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Sub Unit Name",
      dataIndex: "subUnitName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Organisation Name",
      dataIndex: "orgName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      width: "10%",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <Row gutter={18}>
            <Col>
              <Tooltip title="Edit" placement="top" color="#3D4998">
                <EditOutlined
                  style={{
                    color: "#444653",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setOpenDesignationModel({
                      open: true,
                      id: value,
                    });
                  }}
                />
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title="Delete" placement="top" color="red">
                <DeleteOutlined
                  style={{
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setDeleteConfirmation({
                      open: true,
                      id: value,
                    })
                  }
                />
              </Tooltip>
            </Col>
          </Row>
        </Skeleton>
      ),
    },
  ];

  return (
    <Card
      size="small"
      className="TableCard"
      style={{
        marginTop: 35,
        boxShadow: "0px 0px 10px 0px #96A1C84D",
      }}
    >
      <div>
        <Row justify="space-between">
          <Col>
            <Row gutter={15}>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setOpenDesignationModel({
                      open: true,
                      id: "",
                    })
                  }
                >
                  Designation
                </Button>
              </Col>
            </Row>
          </Col>
          <Col>
            <Input
              allowClear
              style={{ width: "220px" }}
              placeholder="Search designation"
              onChange={(e: any) => {
                setDataLoading(true);
                if (!e.cancelable) {
                  const searchItemGroup = setTimeout(() => {
                    setSearchDesignation(e.target.value);
                  }, 1000);
                  return () => clearTimeout(searchItemGroup);
                } else {
                  // console.log("Click cancle............");
                  setSearchDesignation(e.target.value);
                }
              }}
              suffix={
                dataLoading || searchDesignation ? "" : <SearchOutlined />
              }
            />
          </Col>
        </Row>
        <Table
          loading={dataLoading}
          className={`Tabel-style table-${
            themeData?.componentSize ?? "middle"
          }`}
          style={{ width: "100%", marginTop: "10px" }}
          columns={depttCols}
          dataSource={designationsList}
        />
      </div>
      {openDesignationModel && (
        <AddDesignationModel
          isModalOpen={openDesignationModel.open}
          setIsModalOpen={setOpenDesignationModel}
          id={openDesignationModel.id}
          setTrigger={setAddUpdateTrigger}
        />
      )}
      {deleteConfirmation && (
        <ConfirmationModal
          open={deleteConfirmation.open}
          onNo={() =>
            setDeleteConfirmation({
              open: false,
              id: "",
            })
          }
          onYes={() => handleDelete(deleteConfirmation?.id)}
          text={`Are you sure you want to delete this row?`}
        />
      )}
    </Card>
  );
};

export default Designations;
